import React, { useContext, useState } from "react";
import Spinner from "../components/Spinner";
import { toast } from "react-toastify";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { CreateListingComponent } from "../components/CreateListing";
import { LocationListingComponent } from "../components/LocationListing";
import { DescriptionListingComponent } from "../components/DescriptionListing";
import { FeatureAndAmenitiesListingComponent } from "../components/FeatureAndAmenitiesListing";
import { FileListingComponent } from "../components/FileListing";
import { FACILITY_TYPES, OPERATION_TYPES } from "../shared/constants";
import { useFetchCommunities } from "../hooks/useFetchCommunities";
import { AgentContext } from "../context/AgentContext";
import { getText } from '../shared/customization';

export default function CreateListing() {

  const navigate = useNavigate();

  const auth = getAuth();
  const { data: communities } = useFetchCommunities({});
  const { fetchFilteredData } = useContext(AgentContext);

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    type: OPERATION_TYPES[0].key,
    totalArea: 0,
    usefulArea: 0,
    regularPrice: 0,
    title: "",
    description: "",
    images: [],
    videos: [],
    outdoorNumber: 0,
    interiorNumber: 0,
    cp: 0,
    property: FACILITY_TYPES[0].key,
    bedrooms: 0,
    bathrooms: 0,
    garage: false,
    antiquity: 0,
    swimmingPool: false,
    garden: false,
    greenAreas: false,
    terraces: false,
    lobby: false,
    surveillance: false,
    internetAccess: false,
    gym: false,
    serviceRoom: false,
    multipurposeRoom: false,
    community: ""
  });

  const {
    type,
    totalArea,
    usefulArea,
    regularPrice,
    title,
    description,
    images,
    videos,
    property,
    bedrooms,
    bathrooms,
    garage,
    antiquity,
    swimmingPool,
    garden,
    greenAreas,
    terraces,
    lobby,
    surveillance,
    internetAccess,
    gym,
    serviceRoom,
    multipurposeRoom,
    community
  } = formData;

  const handleNext = () => {
    setActiveStep((nexStep) => nexStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // dropdown
  function handleChange(e) {
    let boolean = null;
    // Define boolean
    if (e.target.value === "true") {
      boolean = true;
    }

    if (e.target.value === "false") {
      boolean = false;
    }

    // Text & Boolean & Number
    if (!e.target.files) {
      setFormData((prevState) => ({
        ...prevState,
        [e.target.id]: boolean ?? e.target.value,
      }));
    }
  }

  function handleLocationChange(e) {
    setFormData((prevState) => ({
      ...prevState,
      ['location']: e,
    }));
  }

  function handleChangeFiles(files) {
    if (files.length > 0) {
      const images = [...files].filter((file) => file.type.includes("image"));
      const videos = [...files].filter((file) => file.type.includes("video"));

      setFormData((prevState) => {
        return {
          ...prevState,
          images: [...prevState.images, ...images],
          videos: [...prevState.videos, ...videos],
        };
      });
    }
  }

  const handleDeleteFile = (fileType, index) => {
    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData };
      if (fileType.includes("image")) {
        updatedFormData.images.splice(index, 1);
      } else if (fileType.includes("video")) {
        updatedFormData.videos.splice(index, 1);
      }
      return updatedFormData;
    });
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    if (images.length > 7) {
      setLoading(false);
      toast.error(getText('CreateListing.MaxImagesError'));
      return;
    }

    if (videos.length > 2) {
      setLoading(false);
      toast.error(getText('CreateListing.MaxVideosError'));
      return;
    }

    async function storeImage(image) {
      return new Promise((resolve, reject) => {
        const storage = getStorage();
        const fileName = `${auth.currentUser.uid}-${image.name}-${uuidv4()}`;
        const storageRef = ref(storage, fileName);
        const uploadTask = uploadBytesResumable(storageRef, image);
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            // Observe state change events such as progress, pause and resume
            // Get task progress, including the number of bytes uploaded and the total of bytes to be uploaded
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log("Upload is " + progress + "% done");
            // eslint-disable-next-line default-case
            switch (snapshot.state) {
              case "paused":
                console.log("Upload is paused");
                break;
              case "running":
                console.log("Upload is running");
                break;
            }
          },
          (error) => {
            // Handle unsuccessful uploads
            console.error(error);
            setLoading(false);
            toast.error(getText('CreateListing.ImageUploadError'));
          },
          () => {
            // Handle successful uploads on complete
            // For instance, get the download URL: https://firebasestorage.googleapis.com/...
            getDownloadURL(uploadTask.snapshot.ref).then((downloadUrl) => {
              resolve({ downloadUrl, fileName });
            });
          }
        );
      });
    }

    const imgFiles = await Promise.all(
      [...images].map(async (image) => {
        const { downloadUrl, fileName } = await storeImage(image.file);
        const { file, ...restImage } = image;
        return { ...restImage, savedUrl: downloadUrl, savedFileName: fileName }
      })
    ).catch((error) => {
      console.error(error);
      setLoading(false);
      toast.error(getText('CreateListing.ImagesNotUploaded'));
      return;
    });

    const videoFiles = await Promise.all(
      [...videos].map(async (video) => {
        const { downloadUrl, fileName } = await storeImage(video.file);
        const { file, ...restVideo } = video;
        return { ...restVideo, savedUrl: downloadUrl, savedFileName: fileName }
      })
    ).catch((error) => {
      console.error(error);
      setLoading(false);
      toast.error(getText('CreateListing.VideosNotUploaded'));
      return;
    });

    if(!imgFiles) {
      toast.error(getText('CreateListing.CannotSaveImages'));
    } else {
      const formDataCopy = {
        ...formData,
        imgFiles,
        videoFiles,
        timestamp: serverTimestamp(),
        userRef: auth.currentUser.uid,
        enabled: true
      };
  
      delete formDataCopy.images;
      delete formDataCopy.videos;
      console.log("before save formDataCopy -> ",formDataCopy);
      const docRef = await addDoc(collection(db, "listings"), formDataCopy);

      toast.success(getText('CreateListing.Success'));
      // refresh data from BE
      fetchFilteredData();

      setLoading(false);
      navigate(`/category/${formDataCopy.type}/${docRef.id}`);
    }
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="max-w-md mx-auto px-0 md:px-5">
      <form onSubmit={handleSubmit}>
        {activeStep === 0 && (
          <CreateListingComponent
            onChange={handleChange}
            options={{ communities }}
            type={type}
            regularPrice={regularPrice}
            totalArea={totalArea}
            usefulArea={usefulArea}
            property={property}
            bedrooms={bedrooms}
            bathrooms={bathrooms}
            garage={garage}
            antiquity={antiquity}
            community={community}
          />
        )}

        {activeStep === 1 && (
          <LocationListingComponent onChange={handleLocationChange} />
        )}

        {activeStep === 2 && (
          <DescriptionListingComponent
            onChange={handleChange}
            title={title}
            description={description}
          />
        )}

        {activeStep === 3 && (
          <FeatureAndAmenitiesListingComponent
            onChange={handleChange}
            swimmingPool={swimmingPool}
            garden={garden}
            greenAreas={greenAreas}
            terraces={terraces}
            lobby={lobby}
            surveillance={surveillance}
            internetAccess={internetAccess}
            gym={gym}
            serviceRoom={serviceRoom}
            multipurposeRoom={multipurposeRoom}
          />
        )}

        {activeStep === 4 && (
          <>
            <FileListingComponent
              onChange={handleChangeFiles}
              onDeleteFile={handleDeleteFile}
              imgFiles={images}
              videoFiles={videos}
            />
          </>
        )}

        {/* buttons */}
        {activeStep < 4 && (
          <>
            <div className="flex justify-between mt-6 mb-6">
              <button
                type="button"
                onClick={handleBack}
                className="bg-[#18446b] hover:bg-[#18446b] text-white text-sm px-4 py-2
                            font-semibold uppercase rounded"
              >
                {getText('CreateListing.Back')}
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="bg-[#18446b] hover:bg-[#18446b] text-white text-sm px-4 py-2
                            font-semibold uppercase rounded"
              >
                {getText('CreateListing.Next')}
              </button>
            </div>
          </>
        )}

        {activeStep === 4 && (
          <>
            <div className="flex justify-between mt-6 mb-6">
              <button
                type="button"
                onClick={handleBack}
                className="bg-[#18446b] hover:bg-[#18446b] text-white text-sm px-4 py-2
                            font-semibold uppercase rounded"
              >
                {getText('CreateListing.Back')}
              </button>
              <button
                type="submit"
                className="bg-[#18446b] hover:bg-[#18446b] text-white text-sm px-4 py-2
                            font-semibold uppercase rounded"
              >
                {getText('CreateListing.Create')}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
